import { ADMIN_ROUTE_PATHS } from 'core/constants/admin-route-paths';
import { resolvePeopleListContext } from 'core/constants/people-list-context';
import { NavItem } from 'core/models/nav-item.model';
import { Role } from 'core/models/role.model';

function pathOf(url: string): string {
  return url.split('?')[0] || '/';
}

function routeMatchesPath(route: string, path: string): boolean {
  if (!route) {
    return false;
  }

  if (route === '/') {
    return path === '/';
  }

  return path === route || path.startsWith(`${route}/`);
}

/** First URL segment for module-scoped routes (e.g. `/classes/list` → `/classes`). */
function modulePrefixOf(route: string): string | null {
  const segments = route.split('/').filter(Boolean);
  if (segments.length < 2) {
    return null;
  }

  return `/${segments[0]}`;
}

function pathSegmentAt(routeOrPath: string, index: number): string | null {
  const segments = routeOrPath.split('/').filter(Boolean);
  return segments[index] ?? null;
}

/** Sibling CRUD segments that must not inherit list/basic-data module highlight. */
const MODULE_SIBLING_EXCLUDED_FROM_LIST = new Set(['create', 'trash']);

/**
 * Rotas com item proprio no menu lateral: nao herdam o highlight do list/dashboard
 * do mesmo modulo (ex.: Meu QR sob Controle de acesso / Portaria).
 */
const MODULE_STANDALONE_LEAF_ROUTES: readonly string[] = [
  ADMIN_ROUTE_PATHS.schoolAccessExitMyQr,
  ADMIN_ROUTE_PATHS.schoolAccessPickupMyQr,
  ADMIN_ROUTE_PATHS.pickupDeskMyQr,
];

/**
 * Modulos de Vincular: create/update fazem parte do mesmo fluxo da listagem
 * (nao ha item separado em Cadastrar), entao /modulo/create deve marcar o item da lista.
 */
const LINK_MODULES_WHERE_CREATE_ACTIVATES_LIST = new Set([
  '/student-guardians',
  '/enrollments',
  '/class-teachers',
]);

function pathIsStandaloneLeaf(path: string): boolean {
  return MODULE_STANDALONE_LEAF_ROUTES.some((leaf) =>
    routeMatchesPath(leaf, path),
  );
}

function pathMatchesItemRoute(item: NavItem, path: string): boolean {
  if (routeMatchesPath(item.route, path)) {
    return true;
  }

  // Folha com item proprio no menu: so a propria rota (nao herda /modulo/*).
  if (pathIsStandaloneLeaf(item.route)) {
    return false;
  }

  const itemAction = pathSegmentAt(item.route, 1);

  // Cadastrar / Lixeira children: exact route only (avoid cross-highlight in grouped nav).
  if (itemAction === 'create' || itemAction === 'trash') {
    return false;
  }

  const modulePrefix = modulePrefixOf(item.route);
  if (!modulePrefix || modulePrefix === item.route) {
    return false;
  }

  if (!routeMatchesPath(modulePrefix, path)) {
    return false;
  }

  // Folhas com menu proprio nao devem marcar o list/dashboard do modulo.
  if (pathIsStandaloneLeaf(path) && item.route !== path) {
    return false;
  }

  const pathAction = pathSegmentAt(path, 1);
  if (pathAction && MODULE_SIBLING_EXCLUDED_FROM_LIST.has(pathAction)) {
    if (
      pathAction === 'create' &&
      LINK_MODULES_WHERE_CREATE_ACTIVATES_LIST.has(modulePrefix)
    ) {
      return true;
    }
    return false;
  }

  return true;
}

export function isNavItemActive(item: NavItem, url: string): boolean {
  const [rawPath, queryString] = url.split('?');
  const path = rawPath || pathOf(url);
  const params = new URLSearchParams(queryString ?? '');

  if (item.children?.length) {
    if (pathMatchesItemRoute(item, path)) {
      return true;
    }

    return item.children.some((child) => isNavItemActive(child, url));
  }

  if (!item.route) {
    return false;
  }

  if (!pathMatchesItemRoute(item, path)) {
    return false;
  }

  if (item.queryParams && Object.keys(item.queryParams).length > 0) {
    return Object.entries(item.queryParams).every(([key, value]) => {
      const actual = params.get(key);
      if (key === 'tab' && value === 'attendance') {
        return actual === value || actual === null || actual === '';
      }
      return actual === value;
    });
  }

  if (item.route === ADMIN_ROUTE_PATHS.userCreate) {
    return (
      resolvePeopleListContext({
        from: params.get('from'),
        role: params.get('role') as Role | null,
      }) === 'users' &&
      !params.get('role') &&
      !params.get('from')
    );
  }

  return true;
}
